import { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router';
import './FormConfigPage.css';
import { useJobType } from '../hooks/useJobTypes';
import { type Surface, surfaceAllows } from '@capture/shared';
import { useFormConfigStore } from './state/store';
import { JobTypeSurfaceLink } from '../components/JobTypeSurfaceLink';
import { FieldListPane } from './fields/FieldListPane';
import { FieldEditorDrawer } from './fields/FieldEditorDrawer';
import { HistoryTab } from './history/HistoryTab';
import { ViewVersionModal } from './history/ViewVersionModal';
import { CherryPickModal, type CherryPickSelection } from './history/CherryPickModal';
import { SampleBatchTab } from './sample-data/SampleBatchTab';
import { RecognitionTabContainer } from './recognition/RecognitionTabContainer';
import { formConfigApi, type JobTypeVersion } from './api/client';
import { FormPagesTab } from './forms/FormPagesTab';
import { FormSelector } from './forms/FormSelector';
import { AddFormDialog } from './forms/AddFormDialog';
import { ManageFormsDialog } from './forms/ManageFormsDialog';
import { useJobTypeForms, type CreatedJobTypeForm } from './forms/useJobTypeForms';
import { ExportMappingTab } from './export/ExportMappingTab';
import { FormRemittancePanel } from './remittance/FormRemittancePanel';
import { ZonesTab } from '../components/ZonesTab';
import { OcrProfilesPanel } from '../components/OcrProfilesPanel';
import { ConfirmModal } from '@/components/ConfirmModal';
import { SetupCompass } from '../components/SetupCompass';
import { useWorkspaceContext } from '../workspace/workspace-context';
import { LayoutsPanel } from './layouts/LayoutsPanel';

type Tab = 'fields' | 'recognition' | 'zones' | 'ocr-profiles' | 'export-mapping' | 'form-pages' | 'history' | 'sample' | 'layouts';

// SP1 §5.1 — Form Config tabs are surface-scoped; all current tabs are
// undeclared (= both surfaces). Filtered at render against the job's surface.
const FORM_CONFIG_TABS: ReadonlyArray<{ id: Tab; surfaces?: readonly Surface[] }> = [
  { id: 'fields' }, { id: 'recognition' }, { id: 'zones' }, { id: 'ocr-profiles' },
  { id: 'export-mapping' }, { id: 'form-pages' }, { id: 'history' }, { id: 'sample' },
  { id: 'layouts' },
];

// Header + Pipeline moved to Job Config (2026-06-28). Redirect their old
// Form Config deep links so bookmarks land on the right surface. P3c
// (2026-07-08) — the retired Rules tab is replaced by the Automations tab,
// which lives on Job Config, so ?tab=rules redirects there as ?tab=automations.
// The value is the destination Job Config tab id (usually identical to the key).
const RELOCATED_TABS: Record<string, string> = {
  header: 'header',
  pipeline: 'pipeline',
  rules: 'automations',
};

export function FormConfigPage() {
  const { jobTypeId = '' } = useParams<{ jobTypeId: string }>();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as Tab) ?? 'fields';

  // Bookmarks/links to the relocated Header & Pipeline tabs land here; bounce
  // them to the Job Config surface (runs unconditionally, above the guards).
  useEffect(() => {
    const t = params.get('tab');
    const dest = t ? RELOCATED_TABS[t] : undefined;
    if (dest) {
      navigate(`/admin/job-types/${jobTypeId}?tab=${dest}`, { replace: true });
    }
  }, [params, jobTypeId, navigate]);

  // Plan 2 Task 3 — the page owns the active form. The selected form id lives
  // in the `?form=<id>` query param, defaulting to the default form's id once
  // the forms list loads. A stale `?form=` (an id not in the list) also falls
  // back to the default form.
  const { forms } = useJobTypeForms(jobTypeId);
  const formParam = params.get('form');
  const defaultFormId = forms.find((f) => f.isDefault)?.id ?? forms[0]?.id ?? null;
  const activeFormId =
    formParam && forms.some((f) => f.id === formParam) ? formParam : defaultFormId;

  const selectForm = useFormConfigStore((s) => s.selectForm);
  const storeActiveFormId = useFormConfigStore((s) => s.activeFormId);

  // Keep the store's activeFormId (which drives field/rule/recognition writes)
  // in sync with the page's resolved id. The store seeds activeFormId to the
  // default form on content load (loadVersion); this reconciles it with the
  // URL once forms load and whenever the operator switches forms.
  useEffect(() => {
    if (activeFormId && activeFormId !== storeActiveFormId) {
      selectForm(activeFormId);
    }
  }, [activeFormId, storeActiveFormId, selectForm]);

  // Open state for the Add/Manage dialogs (the dialogs themselves are Tasks
  // 4/5 — here we only hold the boolean state and wire the selector callbacks).
  const [addFormOpen, setAddFormOpen] = useState(false);
  const [manageFormsOpen, setManageFormsOpen] = useState(false);

  const { versions, loading, error } = useWorkspaceContext();
  // Followups #7 — fetch the JobType so the title can include its name,
  // matching the symmetric "Form Config · [Name]" / "Job Config · [Name]"
  // labeling on the other surface. (Renamed from "Processing Config" in
  // WU-10 / R-130.)
  const { jobType } = useJobType(jobTypeId);

  const version = useFormConfigStore((s) => s.version);
  const content = useFormConfigStore((s) => s.content);

  const [viewing, setViewing] = useState<JobTypeVersion | null>(null);
  const [cherryPicking, setCherryPicking] = useState<JobTypeVersion | null>(null);
  // Hooks must be unconditional and order-stable — these stay ABOVE the
  // loading/error/empty guards below (R-72: a cold loading→loaded transition
  // would otherwise call these on a later render than the first, tripping
  // React's "rendered more hooks than during the previous render" #310).
  const [restoreTarget, setRestoreTarget] = useState<JobTypeVersion | null>(null);

  // #A6 — Modals are mounted at the page level (so Radix portals + focus
  // trap behave correctly), but the page-level mount means tab navigation
  // does NOT unmount them. Clear modal state whenever the active tab
  // changes so a stale History-tab modal doesn't follow the user around.
  useEffect(() => {
    setViewing(null);
    setCherryPicking(null);
  }, [tab]);

  // The draft lifecycle (DraftBanner, publish/discard/conformance) now lives in
  // JobVersionWorkspace, the shared parent layout route, which also owns the
  // draft store load. `loading`/`error` come through the workspace context: the
  // provider stays non-blocking so Job Config's non-draft REST tabs render with
  // no draft (§3.4), but Form Config IS draft-bound, so it keeps its original
  // spinner + "Initialize a draft" recovery here (§3.2).
  if (loading) return <div>Loading…</div>;
  if (error) {
    return (
      <div className="admin-error" role="status">
        <p>No draft loaded for this job type.</p>
        <p>This is the expected state after discarding a draft, or for a job type that hasn't been configured yet. Initialize a draft to start editing.</p>
        <button
          className="rb-btn rb-btn-save"
          onClick={async () => {
            await formConfigApi.createDraft(jobTypeId);
            window.location.reload();
          }}
        >
          Initialize a draft
        </button>
      </div>
    );
  }
  if (!version || !content) return <div>Loading…</div>;

  function handleRestore(v: JobTypeVersion) {
    setRestoreTarget(v);
  }

  async function handleConfirmRestore() {
    if (!restoreTarget) return;
    const v = restoreTarget;
    setRestoreTarget(null);
    await formConfigApi.restore(jobTypeId, v.id, { replace: true });
    window.location.reload();
  }

  async function handleCherryPick(selection: CherryPickSelection) {
    if (!cherryPicking) return;
    await formConfigApi.cherryPick(jobTypeId, { sourceVersionId: cherryPicking.id, selection });
    setCherryPicking(null);
    window.location.reload();
  }

  // setParams REPLACES the whole query string, so always merge against the
  // current params — otherwise switching tabs would drop `?form=` and vice
  // versa. The functional updater receives the current params; we clone, set
  // the one key, and return.
  function setParam(key: string, value: string) {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set(key, value);
      return next;
    });
  }

  function setTab(next: Tab) {
    setParam('tab', next);
  }

  function handleSelectForm(formId: string) {
    setParam('form', formId);
    selectForm(formId);
  }

  // F-009 — Add Form appends the new form's content slice to the DRAFT
  // server-side, but the store's contentV3 was loaded at page open and never
  // refreshed: activeForm() fell back to Default (every field write landed
  // there) and the wholesale autosave clobbered the server-appended slice.
  // Wave F 19c — the create/clone responses carry the updated draft
  // (`draftVersion`), so absorb it directly; the createDraft round-trip
  // remains only as the fallback for a response without it.
  async function handleFormCreated(form: CreatedJobTypeForm) {
    try {
      const draft = form.draftVersion ?? (await formConfigApi.createDraft(jobTypeId));
      useFormConfigStore.getState().absorbServerForm(draft, form.id);
    } catch {
      // Without the slice, selecting the form would reproduce the F-009 data
      // loss (writes silently landing on Default). A hard reload re-fetches
      // the draft with the slice intact — the safe fallback.
      window.location.reload();
      return;
    }
    handleSelectForm(form.id);
  }

  // Single resolution of the job's surface (defaulting absent/pre-SP1
  // snapshots to 'standard') — both the tab-strip filter and the remittance
  // panel gate below must read the SAME value, so this is computed once
  // rather than re-deriving the `jobType?.surface ?? 'standard'` fallback
  // at each call site.
  const jobSurface = (jobType?.surface ?? 'standard') as Surface;

  return (
    <div className="form-config-page form-compact">
      <header style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0 }}>
          Form Config{jobType?.name ? ` · ${jobType.name}` : ''}
        </h1>
        <div style={{ marginLeft: 'auto' }}>
          <JobTypeSurfaceLink jobTypeId={jobTypeId} currentSurface="form" />
        </div>
      </header>

      <SetupCompass
        jobTypeId={jobTypeId}
        activeStepId={
          ["fields", "sample", "export-mapping", "zones", "ocr-profiles"].includes(tab as string)
            ? tab
            : ""
        }
      />

      {/* Plan 2 Task 3 — page-level form selector sits ABOVE the tab bar.
          Switching forms updates `?form=` and the store's activeFormId; Add and
          Manage delegate to dialogs built in Tasks 4/5. */}
      <FormSelector
        jobTypeId={jobTypeId}
        activeFormId={activeFormId}
        onSelectForm={handleSelectForm}
        onAddForm={() => setAddFormOpen(true)}
        onManageForms={() => setManageFormsOpen(true)}
      />

      {/* AddFormDialog — Task 4. On created, absorb the server-appended form
          slice into the store (F-009), then select the new form (updates
          ?form= + store activeFormId) so its tabs are immediately in scope. */}
      {/* Conditionally mounted so each dialog's useJobTypeForms (and any
          unsaved key edit / 409 error state) is fresh per open — and so the
          two extra GET /forms don't fire on every page load. The components
          still self-gate on `open`, kept harmless for their internal logic. */}
      {addFormOpen && (
        <AddFormDialog
          jobTypeId={jobTypeId}
          open={addFormOpen}
          onClose={() => setAddFormOpen(false)}
          onCreated={(form) => void handleFormCreated(form)}
        />
      )}
      {/* ManageFormsDialog — Task 5 */}
      {manageFormsOpen && (
        <ManageFormsDialog
          jobTypeId={jobTypeId}
          open={manageFormsOpen}
          onClose={() => setManageFormsOpen(false)}
        />
      )}

      <nav className="form-config-page__tabs" role="tablist">
        {FORM_CONFIG_TABS
          .filter((ft) => surfaceAllows(ft, jobSurface))
          // Task 12 — the Layouts tab is hidden entirely (not disabled, not an
          // empty panel) unless this job type has opted into layout variants.
          // No empty tab: an admin who never enabled the feature never sees it.
          .filter((ft) => ft.id !== 'layouts' || jobType?.allowLayoutVariants)
          .map(({ id: t }) => (
            <button key={t} role="tab" aria-selected={tab === t} onClick={() => setTab(t)}>
              {t === 'form-pages' ? 'pages' : t === 'export-mapping' ? 'export' : t}
            </button>
          ))}
      </nav>

      <main>
        {tab === 'fields' && (
          <div className="form-config-page__fields-tab">
            {/* SP6 Task 8 — per-form remittance role/instrument/
             *  appliedAmountField panel, gated on the SAME `jobSurface`
             *  the tab strip above already resolves, so read and gate can
             *  never drift onto different surfaces. */}
            {jobSurface === 'remittance' && (
              <FormRemittancePanel activeFormId={activeFormId} />
            )}
            <FieldListPane activeFormId={activeFormId} />
            {/* P3a (2026-07) — the per-field Stages behavior editor was
             *  retired. Per-stage behavior authoring now lives on the
             *  job-level Automations tab; nothing renders beneath the
             *  field list here. */}
          </div>
        )}
        {tab === 'recognition' && (
          <RecognitionTabContainer
            activeFormId={activeFormId}
            classificationMode={jobType ? (jobType.classificationMode ?? 'off') : undefined}
          />
        )}
        {tab === 'zones' && <ZonesTab jobTypeId={jobTypeId} activeFormId={activeFormId} />}
        {tab === 'ocr-profiles' && <OcrProfilesPanel activeFormId={activeFormId} />}
        {tab === 'form-pages' && <FormPagesTab />}
        {tab === 'export-mapping' && (
          <ExportMappingTab jobTypeId={jobTypeId} activeFormId={activeFormId} />
        )}
        {tab === 'history' && (
          <HistoryTab
            versions={versions}
            onView={setViewing}
            onRestore={handleRestore}
            onCherryPick={setCherryPicking}
          />
        )}
        {tab === 'sample' && (
          <SampleBatchTab jobTypeId={jobTypeId} fields={content.fields.map((f) => ({ name: f.name }))} />
        )}
        {tab === 'layouts' && <LayoutsPanel jobTypeId={jobTypeId} formId={activeFormId} />}
      </main>

      {/* WU-2 — drawer mounts at the page level so it overlays the entire
          fields tab (and survives tab-internal re-renders). The drawer
          uses `selectedFieldName` from the store as its open state. */}
      <FormConfigFieldDrawer />

      {viewing && <ViewVersionModal version={viewing} onClose={() => setViewing(null)} />}
      {cherryPicking && content && (
        <CherryPickModal
          sourceVersion={cherryPicking}
          draftContent={content}
          onApply={handleCherryPick}
          onCancel={() => setCherryPicking(null)}
        />
      )}
      <ConfirmModal
        open={restoreTarget !== null}
        title={restoreTarget ? `Restore v${restoreTarget.versionNumber}?` : ""}
        description="This will replace your current draft with the selected version."
        confirmLabel="Restore"
        onConfirm={handleConfirmRestore}
        onCancel={() => setRestoreTarget(null)}
      />
    </div>
  );
}

/**
 * WU-2 — small wrapper that subscribes to `fieldEditorOpen` and mounts
 * the FieldEditorDrawer at the page level. Keeping this in a child
 * component avoids re-rendering the whole page on every selection
 * change. Closing the drawer clears the selection (single source of
 * truth in the store).
 *
 * R-117: the drawer now opens only when `fieldEditorOpen` is true, which
 * is set by `openFieldEditor()` (called from the ⋮ menu's Edit item).
 * A plain row-click calls `setSelectedField` which highlights the row but
 * does NOT set fieldEditorOpen, so the drawer stays closed.
 */
function FormConfigFieldDrawer() {
  const setSelectedField = useFormConfigStore((s) => s.setSelectedField);
  const fieldEditorOpen = useFormConfigStore((s) => s.fieldEditorOpen);
  if (!fieldEditorOpen) return null;
  return <FieldEditorDrawer onClose={() => setSelectedField(null)} />;
}
